'use client';
import React from 'react';
import { useModel } from '@/context/ModelContext';
import { computeModel, applyScenario } from '@/lib/financial-model';
import { TrendingDown, Minus, TrendingUp } from 'lucide-react';

type ScenarioKey = 'downside' | 'base' | 'upside';

const SCENARIOS: Array<{
  key: ScenarioKey;
  label: string;
  icon: React.ReactNode;
  color: string;
  bg: string;
  border: string;
}> = [
  {
    key: 'downside',
    label: 'DOWNSIDE',
    icon: <TrendingDown size={12} />,
    color: 'var(--downside)',
    bg: 'rgba(224,82,82,0.06)',
    border: 'rgba(224,82,82,0.2)',
  },
  {
    key: 'base',
    label: 'BASE CASE',
    icon: <Minus size={12} />,
    color: 'var(--gold)',
    bg: 'rgba(201,168,76,0.06)',
    border: 'rgba(201,168,76,0.2)',
  },
  {
    key: 'upside',
    label: 'UPSIDE',
    icon: <TrendingUp size={12} />,
    color: 'var(--upside)',
    bg: 'rgba(62,207,142,0.06)',
    border: 'rgba(62,207,142,0.2)',
  },
];

export default function ScenarioSwitcher() {
  const { assumptions, activeScenario, setActiveScenario } = useModel();

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span
        style={{
          fontSize: '0.6rem',
          fontWeight: 700,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: 'var(--text-tertiary)',
          marginRight: 4,
        }}
      >
        Scenario
      </span>
      {SCENARIOS.map((s) => {
        const isActive = activeScenario === s.key;
        const m = computeModel(applyScenario(assumptions, s.key));
        return (
          <button
            key={`scenario-btn-${s.key}`}
            onClick={() => setActiveScenario(s.key)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded transition-all duration-150"
            style={{
              background: isActive ? s.bg : 'transparent',
              border: `1px solid ${isActive ? s.border : 'var(--divider)'}`,
              color: isActive ? s.color : 'var(--text-tertiary)',
              fontSize: '0.65rem',
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}
          >
            <span style={{ color: isActive ? s.color : 'var(--text-tertiary)' }}>{s.icon}</span>
            {s.label}
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.65rem', opacity: 0.8 }}>
              {(m.projectIRR * 100).toFixed(1)}%
            </span>
          </button>
        );
      })}
    </div>
  );
}
